import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Award,
  BedDouble,
  ChevronRight,
  CircleOff,
  Clock,
  GraduationCap,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  MoveHorizontal,
  Plus,
  Sparkles,
  UserRound,
  Waves,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/breast-implant-removal-surgery-mumbai";

const EXPLANT_FAQS = [
  {
    question: "What is en bloc capsulectomy and do I need it?",
    answer:
      "En bloc capsulectomy removes the implant and its surrounding capsule together as a single intact unit without opening the capsule during surgery. It is the most complete removal technique. It is appropriate for patients with BII concerns, silicone rupture, or textured implant history where containing all capsule contents during removal is a priority. It is not required for every patient and is not always safely achievable depending on capsule thickness and proximity to the chest wall. Dr. Jain assesses whether en bloc removal is appropriate and safe for your specific anatomy during consultation.",
  },
  {
    question: "Will my breasts sag after implant removal?",
    answer:
      "This depends on your natural breast tissue, implant size, how long the implants were in place, and your skin elasticity. Patients with adequate natural tissue, smaller implants, and good skin quality often achieve a satisfactory natural result without additional procedures. Patients with significant skin stretch, large implants, or reduced elasticity benefit from a concurrent breast lift. Dr. Jain gives a patient-specific assessment of expected post-removal appearance during consultation.",
  },
  {
    question: "Can I replace my implants with smaller ones instead of removing them entirely?",
    answer:
      "Yes. Patients who are satisfied with having implants but want a size reduction, a different profile, or correction of a complication can have their current implants replaced rather than removed entirely. This is a separate procedure from explant and is planned differently. Dr. Jain discusses which option better addresses your specific concern during consultation.",
  },
  {
    question: "How long does recovery take after breast implant removal?",
    answer:
      "Most patients return to desk work within 2 weeks. Full activity including exercise resumes at 4 to 6 weeks. If a breast lift or fat grafting is performed simultaneously, recovery follows the timeline of those procedures, which is similar.",
  },
  {
    question: "Is complete capsule removal always necessary?",
    answer:
      "No. A soft, non-contracted capsule with no rupture and no clinical concern does not require removal. Removing an uninvolved capsule adds surgical time, complexity, and risk without clinical benefit. Capsule removal is indicated when the capsule is contracted, calcified, contains silicone from a ruptured implant, or when the patient specifically requests complete removal for BII or personal reasons.",
  },
  {
    question: "What is breast implant illness and can removal help?",
    answer:
      "Breast implant illness refers to a range of systemic symptoms some women attribute to their implants, including fatigue, joint pain, cognitive fog, and skin changes. The scientific evidence around BII is still developing and there is no definitive diagnostic test. Some women report significant symptom improvement after complete explant with en bloc capsule removal. Others do not improve. Dr. Jain does not overstate or dismiss these concerns and performs complete removal for patients who want this approach, while being honest about what the current evidence supports.",
  },
  {
    question: "Will there be new scars after implant removal surgery?",
    answer:
      "In most cases, Dr. Jain uses the existing implant scar to access the implant, so no new scar positions are created. If a breast lift is performed simultaneously, additional incision patterns are required depending on the degree of ptosis. These are positioned in the same locations used for any standard mastopexy and fade over 12 to 18 months.",
  },
  {
    question: "Can I have fat grafting after implant removal to restore some volume?",
    answer:
      "Yes. Fat transfer to the breast after explant is a practical option for patients who want to restore modest volume using their own tissue rather than a new implant. The procedure adds approximately half to one cup size of volume per session and requires adequate donor fat in other body areas. It is performed simultaneously with explant or as a separate procedure once the breast has healed after removal.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["MedicalProcedure", "SurgicalProcedure"],
      name: "Breast implant removal surgery (explant)",
      alternateName: ["Explant surgery", "Breast explant", "Capsulectomy with implant removal"],
      description:
        "Surgical removal of breast implants with capsule management (simple removal, partial, total, or en bloc capsulectomy) and optional mastopexy or fat grafting; for capsular contracture, rupture, BII concerns, or personal choice.",
      procedureType: "Surgical",
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Breast" },
      howPerformed:
        "General anesthesia; access via prior incision when possible; implant removal; capsule assessment and partial/total/en bloc excision as indicated; concurrent lift or fat grafting when planned; support bra; drains when needed; 1.5–3 hours; often overnight stay.",
      url: PAGE_URL,
      additionalProperty: [
        { "@type": "PropertyValue", name: "Procedure category", value: "Surgical" },
        {
          "@type": "PropertyValue",
          name: "Common topics",
          value: "En bloc capsulectomy; breast implant illness (BII); capsular contracture; implant exchange vs removal",
        },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: EXPLANT_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function ExplantJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Breast", href: "/#services" },
    { name: "Breast Implant Removal", href: "/breast-implant-removal-surgery-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Breast</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-5">
              Breast Implant Removal Surgery
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg text-deep-navy/70 mb-8">
              Explant Surgery by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-3">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Breast Implant Removal" icon="🌸" size="hero"  imageSrc={HERO.breastImplantRemoval} imageAlt="Breast implant removal"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">What is Breast Implant Removal Surgery?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Breast implant removal surgery, called explant surgery, removes previously placed breast implants along with some or
              all of the surrounding scar capsule, and reshapes the breast for a natural appearance. It is performed for medical
              reasons, personal preference, or both. The decision to remove implants is as valid as the original decision to place
              them, and the procedure is well-established with predictable outcomes when planned correctly.
            </p>
            <p>
              The most important planning decision in explant surgery is what to do with the capsule. Every implant develops a
              surrounding scar capsule as part of the body&apos;s normal response to a foreign material. In most cases this capsule
              is soft and causes no problems. In some patients it thickens and tightens, causing capsular contracture. In patients
              with implant rupture, the capsule may contain silicone material. In patients concerned about breast implant illness
              symptoms, complete capsule removal is often requested. The appropriate capsule management depends on the clinical
              situation.
            </p>
            <p>
              Dr. Vicky Jain performs breast implant removal surgery as a standalone procedure and in combination with
              breast lift or fat grafting for patients who want to improve breast appearance after removal. Many patients are
              concerned about how their breasts will look after explant. The outcome depends on the original breast tissue, the
              implant size, how long the implants were in place, and skin elasticity. Dr. Jain discusses what is achievable for each
              patient&apos;s specific anatomy during consultation.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Surgical Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Explant Surgery</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                Dr. Jain plans every explant procedure around three questions: why the implants are being removed, what the capsule
                situation requires, and what the breast will look like afterward and whether any concurrent procedure improves that
                outcome. These three questions together determine the surgical plan. A patient removing implants due to uncomplicated
                personal preference has a different plan than a patient with Grade 3 capsular contracture or a ruptured implant with
                silicone migration.
              </p>
              <p>
                For patients concerned about breast implant illness, Dr. Jain discusses the evidence around BII honestly. The
                scientific literature on BII is still developing. Some patients report significant symptom improvement after explant and
                complete capsule removal. Others do not. Dr. Jain does not overstate or dismiss these concerns. He performs the
                procedure the patient requests, explains what the evidence currently supports, and documents the surgery thoroughly for
                patients who want a complete record of what was found and removed.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ReasonsSection() {
  const cards = [
    {
      title: "Capsular Contracture",
      icon: AlertTriangle,
      copy: "The scar capsule around the implant tightens excessively, making the breast feel hard, appear distorted, and in some cases causing pain. Capsulectomy alongside implant removal addresses both the implant and the contracted capsule causing the problem.",
    },
    {
      title: "Implant Rupture",
      icon: CircleOff,
      copy: "Silicone implant rupture is often silent, meaning the implant shell has failed but the cohesive gel has not migrated. Saline rupture is immediate and visible as the breast deflates. Both require implant removal and assessment of the surrounding capsule for any gel that has spread beyond the shell.",
    },
    {
      title: "Implant Shifting or Asymmetry",
      icon: MoveHorizontal,
      copy: "Implants that have moved from their original position, dropped too low, or sit unevenly create visible asymmetry. Removal with pocket correction, or replacement with repositioning, addresses the displacement.",
    },
    {
      title: "Breast Implant Illness",
      icon: Activity,
      copy: "Some women report systemic symptoms including fatigue, joint pain, cognitive fog, and skin changes that they attribute to their implants. The science around breast implant illness continues to develop. Dr. Jain performs complete en bloc removal for patients who want this approach and discusses the current evidence honestly during consultation.",
    },
    {
      title: "Personal Choice and Lifestyle Change",
      icon: UserRound,
      copy: "Many patients choose removal because their preferences have changed over time, they want a more natural body, or their implants no longer align with who they are. This is a completely valid reason for explant and requires no medical justification.",
    },
    {
      title: "Rippling or Unnatural Feel",
      icon: Waves,
      copy: "Implant rippling occurs when the edge or surface of the implant is visible or palpable through the overlying tissue. It is more common in patients with thin breast tissue, above-muscle placement, or saline implants. Removal or replacement addresses the cause.",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="reasons-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Why Women Choose Breast Implant Removal</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const EXPLANT_TYPES = [
  {
    title: "Simple Implant Removal",
    body: "The implant is removed and the capsule is left intact. Appropriate when the capsule is soft, there is no rupture, and there is no clinical indication for capsule removal. The fastest and least involved explant approach with the quickest recovery.",
  },
  {
    title: "Partial Capsulectomy",
    body: "Part of the capsule is removed alongside the implant. Used when sections of the capsule are thickened or symptomatic but complete removal is not required or not safely achievable due to capsule proximity to the chest wall or ribs.",
  },
  {
    title: "Total Capsulectomy",
    body: "The entire capsule is removed along with the implant. Used for capsular contracture, silicone rupture with gel spread within the capsule, calcified capsules, or patients who want complete removal of all implant-associated tissue.",
  },
  {
    title: "En Bloc Capsulectomy",
    body: "The implant and surrounding capsule are removed together as a single intact unit without opening the capsule during surgery. This is the most complete removal technique and is requested by patients with BII concerns, textured implant history, or silicone rupture where containing the capsule contents during removal is a priority. It is technically more demanding than standard total capsulectomy and requires adequate tissue between the capsule and chest wall to perform safely.",
  },
  {
    title: "Implant Removal with Breast Lift",
    body: "For patients whose skin has stretched over the implant and will not contract adequately after removal. A mastopexy is performed simultaneously to remove excess skin and reposition the nipple to a more youthful level. The most common combination procedure in explant surgery.",
  },
  {
    title: "Implant Removal with Fat Transfer",
    body: "For patients who want to restore some breast volume after removal using their own fat rather than a new implant. Fat is harvested from a donor area and grafted into the breast to improve contour and softness after explant. Produces a modest volume restoration with no foreign material.",
  },
];

function TypesOfExplantSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="types-explant-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-10 text-center">Types of Breast Implant Removal Surgery</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {EXPLANT_TYPES.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhatToExpectSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-to-expect-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">What to Expect</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">What Happens to the Breast After Explant Surgery?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              This is the question most patients want answered before anything else. The honest answer is that the outcome depends on
              four factors: how much natural breast tissue existed before the implant was placed, how large the implant was, how
              long it was in place, and the patient&apos;s current skin elasticity. Patients who had meaningful natural breast tissue
              before augmentation, had smaller implants, and have good skin quality often achieve a natural result without any
              additional procedure. Patients who had very little natural tissue, had large implants for many years, or have reduced
              skin elasticity will likely have loose skin and reduced projection after removal alone.
            </p>
            <p>
              Dr. Jain assesses these factors during consultation and gives an honest, patient-specific picture of what to expect.
              For patients where the outcome of removal alone is unlikely to be satisfactory, he discusses whether a concurrent lift,
              fat grafting, or replacement with smaller implants produces a better result. No patient should go into explant
              surgery without a clear understanding of what their breasts will look like afterward.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowSurgerySection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">How Breast Implant Removal Surgery is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 1.5 to 3 hours under general anesthesia depending on the complexity of capsule management and whether
              concurrent procedures are performed. Dr. Jain typically uses the existing implant incision scar to access the implant,
              minimising additional scarring.
            </p>
            <p>
              The implant is removed first. The capsule is then assessed and managed according to the plan: left intact for simple
              removal, partially excised, fully excised, or removed en bloc with the implant depending on the clinical indication. If
              a breast lift is being performed simultaneously, this is completed after implant removal through the same or extended
              incisions. If fat grafting is being performed, the donor area liposuction and breast injection are completed as the
              final step.
            </p>
            <p>
              A support bra is applied immediately after surgery. Drains are used in some cases, particularly after complete
              capsulectomy, for the first 24 to 48 hours. Most patients stay one night and go home the following day.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Week 1",
      description:
        "Swelling, bruising, and tightness. Support bra worn continuously. Rest with limited arm movement. Most patients are comfortable moving around the house within 2 to 3 days.",
      icon: BedDouble,
    },
    {
      period: "Week 2",
      description:
        "Light daily activity resumes. Sutures are checked and removed if non-absorbable. Swelling reduces. The breast shape after removal becomes clearer as swelling settles.",
      icon: Monitor,
    },
    {
      period: "Weeks 3 to 4",
      description: "Most patients return to desk work and normal routine. Strenuous activity and heavy lifting remain restricted.",
      icon: Clock,
    },
    {
      period: "Months 2 to 3",
      description:
        "Final breast shape is visible. Skin contraction is complete. If fat grafting was performed, the surviving fat has integrated and the final volume is stable.",
      icon: Sparkles,
    },
  ];
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const VS_REPLACEMENT_ROWS = [
  {
    label: "Goal",
    removal: "Return to natural breast without implants.",
    replacement: "Maintain or modify breast size with new implants.",
  },
  {
    label: "Capsule Management",
    removal: "Partial or complete capsule removal depending on indication.",
    replacement: "Capsule may be removed or used as the new pocket depending on its condition.",
  },
  {
    label: "Post-Surgery Appearance",
    removal: "Natural breast determined by existing tissue and skin. Lift or fat grafting improves this if needed.",
    replacement: "Maintained or altered breast volume with new implants.",
  },
  {
    label: "Foreign Material",
    removal: "None after surgery.",
    replacement: "New implant in place.",
  },
  {
    label: "Best For",
    removal: "Patients who no longer want implants for any reason.",
    replacement: "Patients whose concern is with the current implant specifically, not with having implants in general.",
  },
];

function RemovalVsReplacementSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="removal-vs-replacement-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Implant Removal vs. Implant Replacement</h2>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm md:text-base">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-4 pr-4 font-serif text-premium-purple font-normal w-[22%]" />
                <th scope="col" className="py-4 px-3 font-serif text-premium-purple bg-white/80">
                  Removal
                </th>
                <th scope="col" className="py-4 pl-3 font-serif text-premium-purple bg-white/80">
                  Replacement
                </th>
              </tr>
            </thead>
            <tbody>
              {VS_REPLACEMENT_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-4 pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-4 px-3 text-deep-navy/70 align-top">{row.removal}</td>
                  <td className="py-4 pl-3 text-deep-navy/70 align-top">{row.replacement}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/70 text-center leading-relaxed">
          Some patients are unsure whether to remove or replace. Dr. Jain discusses both options during consultation based on the
          reason for the current dissatisfaction. A patient with capsular contracture may be well-served by replacement with a new
          implant in a new pocket. A patient with BII concerns or a strong preference for a natural body is better served by complete
          removal.
        </p>
      </div>
    </section>
  );
}

function WhoIsGoodCandidateSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-is-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">
            Who Is a Good Candidate for Breast Implant Removal?
          </h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Any woman with breast implants who wants them removed is a candidate for explant surgery. There is no medical requirement
              to keep implants. Patients with active complications including capsular contracture, rupture, infection, or
              significant asymmetry have a clear clinical indication for removal. Patients choosing removal for personal reasons,
              including preference for a natural body, lifestyle change, or BII concerns, are equally appropriate candidates.
            </p>
            <p>
              Good health and stable weight support a better surgical outcome and recovery. Patients who smoke are asked to stop at
              least 4 weeks before surgery as smoking impairs healing and increases complication risk. Patients considering
              simultaneous breast lift should be at a stable weight, as significant weight change after mastopexy affects the lifted
              result. The most important factor in a successful explant outcome is an honest consultation where the expected
              post-removal appearance is discussed clearly and any concurrent procedures to improve that appearance are planned
              together.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Breast" />;
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {EXPLANT_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`explant-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">
          Book Your Breast Implant Removal Consultation
        </h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain reviews your implant history, assesses the current state of your implants and capsules,
          and discusses your reasons for removal to determine the appropriate surgical plan. You receive an honest assessment of what
          your breasts will look like after removal and a clear recommendation on whether any concurrent procedure improves that
          outcome. You leave with a complete plan and no unanswered questions.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Breast Lift",
      description: "Restore breast position and remove excess skin after implant removal for a natural, lifted result.",
      href: "/breast-lift-surgery-mumbai",
    },
    {
      name: "Breast Fat Transfer",
      description: "Restore modest breast volume after explant using your own fat with no new implant.",
      href: "/breast-fat-transfer-lipofilling-mumbai",
    },
    {
      name: "Breast Augmentation",
      description: "Implant replacement or exchange for patients who want to maintain breast volume with updated implants.",
      href: "/breast-implant-surgery-mumbai",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function BreastImplantRemovalPage() {
  return (
    <>
      <SEO
        title="Breast Implant Removal Surgery | Dr. Vicky Jain"
        description="Removing implants due to complications, capsular contracture, or personal choice. Dr. Vicky Jain performs explant surgery safely. Book your consultation."
        canonical="/breast-implant-removal-surgery-mumbai"
      />
      <ExplantJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <ReasonsSection />
      <TypesOfExplantSection />
      <WhatToExpectSection />
      <HowSurgerySection />
      <RecoveryTimelineSection />
      <RemovalVsReplacementSection />
      <WhoIsGoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
